import React from "react";
import PropTypes from "prop-types";
import { injectStripe } from "react-stripe-elements";
import styled from "styled-components";
import { Flex, Box, Text, Heading } from "rebass";
import Img from "react-image";
import FaLock from "react-icons/lib/fa/lock";

import CardSection from "./CardSection";
import Button from "../ui/Button";
import stripeLogo from "../../img/powered_by_stripe@2x.png";
import stripeSecure from "../../img/stripe_secure_small.jpg";

type Props = {
  stripe: {
    createToken: ({ name: string }) => mixed
  }
};

const { Fragment } = React;

class CheckoutForm extends React.Component<Props> {
  constructor(props) {
    super(props);

    this.state = {
      customerName: "",
      showPostCheckout: false,
      submitting: false,
      stripeToken: {}
    };
  }

  setError = (item: string, error: string) => {
    this.setState({
      [item]: error
    });
  };

  setSubmitting = (submitting: boolean) => {
    this.setState({
      submitting
    });
  };

  close = () => {
    window.location.href = "https://www.messenger.com/closeWindow/";

    this.context.mixpanel.track("demo_subscribe_closed");
  };

  // async createCustomer(token) {
  //   try {
  //     const result = await request(`${API}/account/payment`, "post", {
  //       tokenId: token.id,
  //       facebookPageId: this.store.app.facebookPageId
  //     });

  //     if (has(result, "id")) {
  //       this.store.app.getAccountDetails();

  //       if (typeof this.props.onClose === "function") this.props.onClose();
  //       if (typeof this.props.onSuccess === "function") this.props.onSuccess();
  //     } else if (has(result, "message")) {
  //       throw new Error(result.message);
  //     } else {
  //       throw new Error("No response from the server");
  //     }

  //     this.setSubmitting(false);
  //   } catch (error) {
  //     handleErrorResponse(error).then(() => {
  //       const response = `Sorry but we couldn't take your payment! Our team has been notified and we're looking into what happened.${
  //         !isProduction ? ` Server response: ${error}` : ""
  //       }`;
  //       this.setError("serverError", response);
  //     });

  //     this.setSubmitting(false);
  //   }
  // }

  handleStripeSuccess = token => {
    this.setState({
      // stripeToken: token,
      showPostCheckout: true
    });
  };

  handleSubmit = ev => {
    // We don't want to let default form submission happen here, which would refresh the page.
    ev.preventDefault();

    if (!this.state.customerName) {
      this.setError("customerNameError", "Please enter a name...");
      return false;
    }

    this.context.mixpanel.track("demo_subscribe_checkout_click", {
      name: this.state.customerName
    });

    this.setSubmitting(true);

    // Within the context of `Elements`, this call to createToken knows which Element to
    // tokenize, since there's only one in this group.
    return this.props.stripe
      .createToken({ name: this.state.customerName })
      .then(({ token, error }) => {
        if (error) throw new Error(error.message);

        this.handleStripeSuccess(token);
        this.setSubmitting(false);
      })
      .catch(error => {
        this.setError("cardError", error.message);
        this.setSubmitting(false);
      });
  };

  updateCustomerName = (e: { target: { value: string } }) => {
    this.setState({
      customerName: e.target.value
    });
  };

  checkoutRender = () => {
    const {
      customerName,
      submitting,
      customerNameError,
      cardError,
      serverError
    } = this.state;

    return (
      <Fragment>
        {(cardError || serverError) && (
          <ErrorText>
            <span
              role="img"
              className="middle"
              aria-label="Shocked little emoji dude"
            >
              😮
            </span>
            {cardError || serverError}{" "}
          </ErrorText>
        )}

        <label htmlFor="customerName">
          <StyledLock>
            <FaLock size={22} />
          </StyledLock>
          <Text f={[2]} color="support2">
            Cardholder name
          </Text>
          <NameInput
            name="customerName"
            value={customerName}
            onChange={this.updateCustomerName}
            placeholder={customerNameError}
            className={customerNameError ? "invalid" : ""}
            disabled={submitting}
          />
        </label>

        <CardSection disabled={submitting} />

        <Flex align="center">
          <Box>
            <StyledImg
              src={stripeLogo}
              alt="Powered by Stripe"
              width="108px"
              className="middle"
            />
          </Box>
          <Box ml="auto">
            <CheckoutButton
              type="submit"
              disabled={submitting}
              loading={submitting ? "loading" : ""}
            >
              {submitting ? "Subscribing" : "Subscribe"}
            </CheckoutButton>
          </Box>
        </Flex>
      </Fragment>
    );
  };

  postCheckoutRender = () => (
    <Fragment>
      <Heading mb={[3]} f={[3]} color="error">
        Payment declined by Pipeline
      </Heading>

      <Text mb={[3]} f={[2]}>
        We are still in beta and only accepting payments from existing customers
        who qualify for the program. If you&apos;re interested in joining, you
        can do so by sending us a message via Messenger.
      </Text>

      <Text mb={[3]} f={[2]}>
        We have not charged your card.
      </Text>

      <Button mb={[2, 2, 3, 4]} onClick={this.close}>
        Close Demo
      </Button>
    </Fragment>
  );

  render() {
    const { showPostCheckout } = this.state;

    return (
      <Fragment>
        <Text align="center" mb={[3]} color="support2" f="14px">
          <Img
            src={stripeSecure}
            alt="Secure Payments powered by Stripe"
            width="200"
          />
        </Text>
        <Text align="center" mb={[2]} color="support1" f="15px">
          Cancel Any time - No lock in contract
        </Text>
        <Text align="center" mb={[4]} color="support1" f="15px">
          Site encrypted with 128-bit SSL encryption
        </Text>
        <StyledForm onSubmit={this.handleSubmit}>
          {!showPostCheckout
            ? this.checkoutRender()
            : this.postCheckoutRender()}
        </StyledForm>
      </Fragment>
    );
  }
}

CheckoutForm.contextTypes = { mixpanel: PropTypes.object.isRequired };

export default injectStripe(CheckoutForm);

// Styled Components
const StyledForm = styled.form`
  background: ${p => p.theme.colors.gray1};
  border-radius: 5px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.15);
  left: 0;
  margin: 0 auto;
  max-width: 500px;
  overflow: hidden;
  padding: 20px;
  position: relative;
  width: 100%;
  z-index: 3;

  .StripeElement {
    -webkit-transition: box-shadow 150ms ease;
    background-color: white;
    height: 50px;
    padding: 15px 12px;
    border-radius: 4px;
    border: 1px solid transparent;
    box-shadow: 0 1px 3px 0 #e6ebf1;
    margin: 15px 0 20px;
    transition: box-shadow 150ms ease;
  }

  .StripeElement--focus {
    box-shadow: 0 1px 3px 0 #cfd7df;
  }

  .StripeElement--invalid {
    border-color: ${p => p.theme.colors.error};
  }

  .StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
  }
`;

const CheckoutButton = styled(Button)`
  padding-top: 0;
`;

const NameInput = styled.input`
  -webkit-transition: box-shadow 150ms ease;
  background-color: white;
  height: 50px;
  padding: 15px 12px;
  border-radius: 4px;
  border: 1px solid transparent;
  box-shadow: 0 1px 3px 0 #e6ebf1;
  margin: 15px 0 20px;
  transition: box-shadow 150ms ease;
  width: 100%;

  &:focus {
    box-shadow: 0 1px 3px 0 #cfd7df;
    outline: none;
  }

  &.invalid {
    border-color: ${p => p.theme.colors.error};

    &::placeholder {
      color: #e2857d;
    }
  }
`;

const ErrorText = styled(Text)`
  background: ${p => p.theme.colors.error};
  border-radius: 5px;
  color: #fff;
  margin: 0 0 15px 0;
  padding: 16px;
`;

const StyledImg = styled(Img)`
  margin-top: 6px;
`;

const StyledLock = styled.div`
  color: rgba(38, 213, 115, 0.7);
  margin-top: -3px;
  position: absolute;
  right: 20px;
`;



// WEBPACK FOOTER //
// ./src/components/Stripe/CheckoutForm.jsx